import { useEffect, useState } from 'react'

import {cn, isNumber} from '@/lib/utils'
import { Container } from '@/components/ui/section'
import { SubMenuList } from '@/components/custom/header/mainmenu/subMenu/subMenuList'
import { SubMenuTeaser } from '@/components/custom/header/mainmenu/subMenu/subMenuTeaser'
import {MainMenuItemModel} from "@/components/custom/header/mainmenu/mainMenuItem";
import {usePathname} from "next/navigation";

const submenuClasses = {
    default:
        'submenu hidden w-full z-20 bg-gray-beige-light absolute top-full left-0 text-gray-beige-dark overflow-hidden transition-all duration-500 ease-in-out lg:block',
    inactive: 'h-0 invisible'
}

interface SubMenuProps {
    setSubactive: (value: boolean) => void;
    active: boolean;
    items?: MainMenuItemModel[];
    locale: string;
}

export type ActiveSubMenuItem = {
    2: { item: number | null },
    3: { item: number | null },
    4: { item: number | null },
}

const SubMenu = ({ setSubactive, active, items, locale }: SubMenuProps) => {
    const [subMenuItemsLevel2, setSubMenuItemsLevel2] = useState<MainMenuItemModel[] | null>()
    const [subMenuItemsLevel3, setSubMenuItemsLevel3] = useState<MainMenuItemModel[] | null>()
    const [subMenuItemsLevel4, setSubMenuItemsLevel4] = useState<MainMenuItemModel[] | null>()
    const [subMenuTeaserItem, setSubMenuTeaserItem] = useState<MainMenuItemModel | null>()
    const [activeSubMenuItem, setActiveSubMenuItem] = useState<ActiveSubMenuItem>({ 2: { item: null }, 3: { item: null }, 4: { item: null } })
    const [currentSubMenuItem, setCurrentSubMenuItem] = useState<ActiveSubMenuItem>({ 2: { item: null }, 3: { item: null }, 4: { item: null } });
    const [height, setHeight] = useState(0)
    const [listHeights, setListHeights] = useState({})

    const pathname = usePathname();
    const ids = pathname.split("/").filter(v => isNumber(v)).map(Number);

    useEffect(() => {
        if(ids.length === 1 && pathname.includes("product")) {
            setCurrentSubMenuItem({
                2: { item: ids[0] },
                3: { item: null },
                4: { item: null }
            })
        } else if(ids.length === 2 && pathname.includes("product")) {
            setCurrentSubMenuItem({
                2: { item: ids[0] },
                3: { item: ids[1] },
                4: { item: null }
            })
        }
    }, [pathname]);

    const handleSubMenuItems = (item: MainMenuItemModel | null, level: number) => {
        if (item?.id) {
            setActiveSubMenuItem((oldState) => {
                return {
                    ...oldState,
                    [level]: { item: item.id }
                }
            })
        }

        if (level === 1) {
            setSubMenuItemsLevel2(items || null)
            setSubMenuItemsLevel3(null)
            setSubMenuItemsLevel4(null)
            setSubMenuTeaserItem(null)
        }
        if (level === 2) {
            setSubMenuItemsLevel3(item?.children || null)
            setSubMenuItemsLevel4(null)
            setSubMenuTeaserItem(null)
        }
        if (level === 3) {
            setSubMenuItemsLevel4(item?.children || null)
            setSubMenuTeaserItem(null)
        }
        if (item?.teaser) {
            setSubMenuTeaserItem(item)
        } else {
            setSubMenuTeaserItem(null)
        }
    }

    function handleMouseLeave(e: React.MouseEvent<HTMLDivElement>) {
        setSubactive(false);
        setActiveSubMenuItem({ 2: { item: null }, 3: { item: null }, 4: { item: null } });
    }

    useEffect(() => handleSubMenuItems(null, 1), [items])
    useEffect(() => {
        const arr: number[] = Object.values(listHeights)
        const maxHeight = Math.max(...arr) + 40
        setHeight(maxHeight)
    }, [listHeights])

    return (
        <div
            className={cn(submenuClasses['default'])}
            aria-label='Submenu'
            onMouseOver={() => setSubactive(true)}
            onMouseLeave={handleMouseLeave}
            style={active ? { height: `${height}px`, visibility: 'visible' } : { height: '0px', visibility: 'hidden' }}
        >
            <Container flow={false} padding='xs'>
                <SubMenuList
                    items={subMenuItemsLevel2}
                    level={2}
                    locale={locale}
                    activeSubMenuItem={activeSubMenuItem}
                    currentSubMenuItem={currentSubMenuItem}
                    handleSubMenuItems={handleSubMenuItems}
                    setListHeights={setListHeights}
                    setSubactive={setSubactive}
                />
                <SubMenuList
                    className={(subMenuItemsLevel3 && subMenuItemsLevel3.length > 16) ? "grid grid-cols-2" : ""}
                    items={subMenuItemsLevel3}
                    level={3}
                    locale={locale}
                    activeSubMenuItem={activeSubMenuItem}
                    currentSubMenuItem={currentSubMenuItem}
                    handleSubMenuItems={handleSubMenuItems}
                    setListHeights={setListHeights}
                    setSubactive={setSubactive}
                />
                <SubMenuList
                    items={subMenuItemsLevel4}
                    level={4}
                    locale={locale}
                    activeSubMenuItem={activeSubMenuItem}
                    currentSubMenuItem={currentSubMenuItem}
                    handleSubMenuItems={handleSubMenuItems}
                    setListHeights={setListHeights}
                    setSubactive={setSubactive}
                />
                <SubMenuTeaser item={subMenuTeaserItem} level={5} locale={locale} setListHeights={setListHeights} />
            </Container>
        </div>
    )
}

export { SubMenu }
